import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Check, Building2, Briefcase, Globe } from 'lucide-react';
import { toast } from 'react-toastify';

import { useAuth } from '../../contexts/AuthContext';
import { usePublicLanguage } from '../../contexts/PublicLanguageContext';
import AuthBanner from '../../components/auth/AuthBanner';
import { detectBrowserTimezone, getTimezoneOptions } from '../../utils/timezones';
import { authAPI } from '../../utils/api';
import { getFallbackIndustryOptions } from '../../utils/industries';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    industry: '',
    email: '',
    password: '',
    confirmPassword: '',
    timezone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [industryOptions, setIndustryOptions] = useState(() => getFallbackIndustryOptions((k, f) => f || k));
  
  const { signup } = useAuth();
  const { t, currentLanguage } = usePublicLanguage();
  const navigate = useNavigate();
  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  useEffect(() => {
    const detectedTimezone = detectBrowserTimezone();
    setFormData((prev) => ({
      ...prev,
      timezone: prev.timezone || detectedTimezone
    }));
  }, []);

  useEffect(() => {
    let isMounted = true;
    const loadIndustries = async () => {
      try {
        const response = await authAPI.getIndustries(currentLanguage);
        const options = response?.data?.options;
        if (isMounted && Array.isArray(options) && options.length > 0) {
          setIndustryOptions(options);
          return;
        }
      } catch (error) {
        // fallback handled below
      }

      if (isMounted) {
        setIndustryOptions(getFallbackIndustryOptions(t));
      }
    };

    loadIndustries();
    return () => {
      isMounted = false;
    };
  }, [currentLanguage, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear field-specific errors on change
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!formData.email) {
      newErrors.email = t('auth.signup.errors.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('auth.signup.errors.emailInvalid');
    }
    
    // Password validation
    if (!formData.password) {
      newErrors.password = t('auth.signup.errors.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.signup.errors.passwordTooShort');
    }
    
    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.errors.confirmPasswordRequired');
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.errors.passwordsNoMatch');
    }
    
    // Name validation (optional but if provided, should be valid)
    if (formData.name && formData.name.length < 2) {
      newErrors.name = t('auth.signup.errors.nameTooShort');
    }

    if (!formData.companyName || formData.companyName.trim().length < 2) {
      newErrors.companyName = t('auth.signup.errors.companyRequired');
    }

    if (!formData.industry) {
      newErrors.industry = t('auth.signup.errors.industryRequired');
    } else if (!industryOptions.some((option) => option.value === formData.industry)) {
      newErrors.industry = t('auth.signup.errors.industryRequired');
    }

    if (!formData.timezone) {
      newErrors.timezone = t('auth.signup.errors.timezoneRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const result = await signup({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        language: currentLanguage,
        timezone: formData.timezone,
        companyName: formData.companyName,
        industry: formData.industry
      });
      
      if (result.success) {
        if (result.warning) {
          toast.warn(result.warning);
        }
        // Don't show toast here, let LoginPage handle it
        navigate('/login?message=signup-success');
      } else {
        // Handle specific error messages based on error codes
        const errorCode = result.errorCode;
        
        if (errorCode === 'EMAIL_EXISTS') {
          setErrors({ email: result.error });
        } else if (errorCode === 'INVALID_EMAIL') {
          setErrors({ email: result.error });
        } else if (errorCode === 'PASSWORD_TOO_SHORT') {
          setErrors({ password: result.error });
        } else if (errorCode === 'INVALID_NAME') {
          setErrors({ name: result.error });
        } else if (errorCode === 'INVALID_COMPANY') {
          setErrors({ companyName: result.error });
        } else if (errorCode === 'INVALID_INDUSTRY') {
          setErrors({ industry: result.error });
        } else if (errorCode === 'INVALID_TIMEZONE') {
          setErrors({ timezone: result.error });
        } else {
          toast.error(result.error);
        }
      }
    } catch (error) {
      toast.error(t('auth.messages.signupError'));
    } finally {
      setLoading(false);
    }
  };

  // Password strength indicators
  const passwordChecks = [
    { test: formData.password.length >= 8, label: t('auth.signup.passwordChecks.length', 'Au moins 8 caractères') },
    { test: /[A-Z]/.test(formData.password), label: t('auth.signup.passwordChecks.uppercase', 'Une lettre majuscule') },
    { test: /[a-z]/.test(formData.password), label: t('auth.signup.passwordChecks.lowercase', 'Une lettre minuscule') },
    { test: /[0-9]/.test(formData.password), label: t('auth.signup.passwordChecks.number', 'Un chiffre') }
  ];

  return (
    <div className="min-h-screen bg-slate-100 auth-login-page">
      <div className="min-h-screen grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="hidden lg:flex">
          <AuthBanner type="signup" />
        </div>

        <div className="relative flex items-center justify-center px-4 py-10 sm:px-8 lg:px-14 auth-login-form-wrap">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="relative w-full max-w-2xl"
          >
            <motion.form
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              onSubmit={handleSubmit}
              noValidate
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6 auth-login-form-paper"
            >
              <div className="mb-5">
                <h1 className="text-xl font-semibold text-slate-900 auth-login-title">{t('auth.signup.title')}</h1>
                <p className="mt-1 text-sm text-slate-600 auth-login-subtitle">{t('auth.signup.subtitle')}</p>
              </div>

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-medium text-slate-700 auth-login-label">
                    {t('auth.signup.fullName')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <User className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-3 text-slate-900 transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 auth-login-input ${
                        errors.name
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                      placeholder="John Doe"
                    />
                  </div>
                  {errors.name && <p className="mt-2 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label htmlFor="companyName" className="mb-2 block text-sm font-medium text-slate-700 auth-login-label">
                    {t('auth.signup.company')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Building2 className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <input
                      id="companyName"
                      name="companyName"
                      type="text"
                      autoComplete="organization"
                      value={formData.companyName}
                      onChange={handleChange}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-3 text-slate-900 transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 auth-login-input ${
                        errors.companyName
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                      placeholder={t('auth.signup.companyPlaceholder', 'Your company name')}
                    />
                  </div>
                  {errors.companyName && <p className="mt-2 text-sm text-red-600">{errors.companyName}</p>}
                </div>

                <div>
                  <label htmlFor="industry" className="mb-2 block text-sm font-medium text-slate-700 auth-login-label">
                    {t('auth.signup.industry', 'Industry')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Briefcase className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <select
                      id="industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-3 text-slate-900 transition-colors focus:outline-none focus:ring-2 auth-login-input ${
                        errors.industry
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                    >
                      <option value="">{t('auth.signup.selectIndustry', 'Select your industry')}</option>
                      {industryOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.industry && <p className="mt-2 text-sm text-red-600">{errors.industry}</p>}
                </div>

                <div>
                  <label htmlFor="timezone" className="mb-2 block text-sm font-medium text-slate-700 auth-login-label">
                    {t('auth.signup.timezone', 'Time zone')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Globe className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <select
                      id="timezone"
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-3 text-slate-900 transition-colors focus:outline-none focus:ring-2 auth-login-input ${
                        errors.timezone
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                    >
                      <option value="">{t('auth.signup.selectTimezone', 'Select your time zone')}</option>
                      {timezoneOptions.map((timezone) => (
                        <option key={timezone.value} value={timezone.value}>
                          {timezone.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.timezone && <p className="mt-2 text-sm text-red-600">{errors.timezone}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700 auth-login-label">
                    {t('auth.signup.email')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Mail className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-3 text-slate-900 transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 auth-login-input ${
                        errors.email
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                      placeholder="name@company.com"
                    />
                  </div>
                  {errors.email && <p className="mt-2 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="mb-2 block text-sm font-medium text-slate-700 auth-login-label">
                    {t('auth.signup.password')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-10 text-slate-900 transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 auth-login-input ${
                        errors.password
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 text-slate-500 transition-colors hover:text-slate-700 auth-login-eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? t('auth.password.hide') : t('auth.password.show')}
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.password && <p className="mt-2 text-sm text-red-600">{errors.password}</p>}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="confirmPassword" className="mb-2 block text-sm font-medium text-slate-700 auth-login-label">
                    {t('auth.signup.confirmPassword')}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-5 w-5 text-slate-400 auth-hand-icon" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`block w-full rounded-xl border py-3 pl-10 pr-10 text-slate-900 transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 auth-login-input ${
                        errors.confirmPassword
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-100'
                          : 'border-slate-300 focus:border-indigo-500 focus:ring-indigo-100'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 text-slate-500 transition-colors hover:text-slate-700 auth-login-eye-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? t('auth.password.hide') : t('auth.password.show')}
                    >
                      {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>}
                </div>
              </div>

              {formData.password && (
                <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 auth-login-label">
                    {t('auth.signup.passwordChecks.title', 'Password checks')}
                  </p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {passwordChecks.map((check, index) => (
                      <div key={index} className="flex items-center text-xs">
                        <Check className={`mr-2 h-4 w-4 ${check.test ? 'text-emerald-600' : 'text-slate-300'}`} />
                        <span className={check.test ? 'text-emerald-700' : 'text-slate-600'}>{check.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs leading-5 text-slate-600 auth-login-subtitle">{t('auth.signup.terms')}</p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 auth-login-submit"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-b-transparent" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    {t('auth.signup.signupButton')}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>

              <div className="my-6 flex items-center gap-3 auth-login-divider">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-500">{t('auth.signup.or', 'Or')}</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <p className="text-center text-sm text-slate-600 auth-login-subtitle">
                {t('auth.signup.hasAccount')}{' '}
                <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-500 auth-login-link">
                  {t('auth.signup.signIn')}
                </Link>
              </p>
            </motion.form>

            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-slate-500 hover:text-slate-700 auth-login-link">
                ← {t('auth.messages.backToHome')}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
